/**
 * React provider for telemetry
 *
 * Responsibilities:
 * 1. Detects cloud provider (Azure / AWS) from environment configuration
 * 2. Initialises the correct telemetry backend (App Insights or AWS RUM)
 * 3. Provides AppInsightsContext for automatic page view tracking (Azure only)
 * 4. Sets authenticated user context when user logs in
 * 5. Clears user context on logout
 * 6. Flushes telemetry on page unload
 *
 * Position in provider tree: inside AuthProviderWithNavigation (needs auth context),
 * outside WeBuildClientProvider (so API clients can use the logger).
 */

import { useEffect } from "react";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { useAuth, useUser } from "@webuild-ai/react-auth";

import { siteConfig } from "@/config/site";

import { initialiseAppInsights, getReactPlugin } from "./appInsights";
import { initialiseAwsRum } from "./backends/awsRumBackend";
import { createAppInsightsBackend } from "./backends/appInsightsBackend";
import { createAwsRumBackend } from "./backends/awsRumBackend";
import { setBackend } from "./logger";
import { logger } from "./logger";
import { detectCloudProvider } from "./cloudProvider";

/** Initialise the correct telemetry backend at module load (before any component renders) */
const config = siteConfig();
const cloud = detectCloudProvider();

if (cloud === "aws") {
  // AWS deployments use CloudWatch RUM
  initialiseAwsRum({
    applicationId: config.aws_rum_application_id,
    applicationVersion: "1.0.0",
    region: config.aws_rum_region ?? "us-east-1",
  });
  setBackend(createAwsRumBackend());
} else {
  // Azure or unknown — fall back to App Insights (gracefully no-ops without a connection string)
  const apiHostname = (() => {
    try {
      return new URL(config.adminAPIURL).hostname;
    } catch {
      return "";
    }
  })();

  initialiseAppInsights({
    connectionString: (import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING as string) || "",
    correlationHeaderDomains: apiHostname ? [apiHostname] : [],
  });
  setBackend(createAppInsightsBackend());
}

export function TelemetryProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { userProfile } = useUser();
  const reactPlugin = cloud === "azure" ? getReactPlugin() : null;

  useEffect(() => {
    if (isAuthenticated && userProfile) {
      logger.setUser({
        id: userProfile.id ?? "",
        email: userProfile.email ?? "",
        name: userProfile.name ?? "",
        isEmbedded: false,
        tenant: userProfile.tenant,
      });
      logger.trackEvent("auth_login", {
        tenant: userProfile.tenant,
      });
    } else {
      logger.clearUser();
    }
  }, [isAuthenticated, userProfile]);

  useEffect(() => {
    const handleBeforeUnload = () => logger.flush();

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Only wrap in AppInsightsContext when using Azure backend with a valid plugin
  if (!reactPlugin) {
    return <>{children}</>;
  }

  return <AppInsightsContext.Provider value={reactPlugin}>{children}</AppInsightsContext.Provider>;
}
